import './UserPage.css'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Form, Spinner } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'

import { usePackageReqs } from '../../../api/package-req'
import { createPackageReq, rejectPackageReq } from '../../../api/package-req/packageReqService'
import { DataGrid } from '../../../Components/DataGrid/DataGrid'
import DropdownMenu from '../../../Components/DropdownMenu'
import { sortPackagesDataByDateArrivedReg } from '../../../helpers/sortByupdateDate'
import checkIcon from '../../../imgs/check.png'
//Icons
import cancelIcon from '../../../imgs/remove.png'
import { QUERY_KEYS } from '../../../lib/queryKeys'
import { deletePackage, getPackages, updatePackage } from '../../../services/packages'
import { confirmUser, deleteUser, getUser, updateUser } from '../../../services/users'
import { PackageRequestDataGrid } from '../PackageRequests/PackageRequestDataGrid'
import { ReturnPackageModal } from './ReturnPackageModal'
import { UnitePackageModal } from './UnitePackageModal'
import { useUserColDefs } from './useUserColDefs'

const formatUser = user => {
  return user.Attributes.reduce((acc, att) => {
    acc[att.Name] = att.Value
    return acc
  }, {})
}

export default function UserPage({ searchedPackage }) {
  const { loggedUser } = useSelector(state => state.user)
  const history = useHistory()
  const { userId } = useParams()

  const [userState, updateUserState] = useState({})
  const [editMode, setEditMode] = useState(false)
  const [itemToReturn, setItemToReturn] = useState(null)
  const [itemToUnite, setItemToUnite] = useState(null)
  const isSeniorAgent = loggedUser.permissions['Admin'] || loggedUser.permissions['SeniorAgent']
  const isAgent = loggedUser.permissions['Admin'] || loggedUser.permissions['Agent']

  const { data: user, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.USER, userId],
    queryFn: async () => {
      const res = await getUser(userId)
      return formatUser(res)
    },
    enabled: !!userId,
  })

  const { data: pkgReqMap } = usePackageReqs({ warehouse: '', userId, userEmail: user?.email, onlyPending: false })
  const { data: packages, isLoading: isPackagesLoading } = useQuery({
    queryKey: [QUERY_KEYS.BACKOFFICE_USER_PACKAGES, userId],
    queryFn: () =>
      getPackages({
        key: {
          index_name: 'costumer-index',
          primary: 'costumer',
          query: {
            costumer: userId,
          },
        },
        attr: { costumer: userId },
      }),
    select: data => {
      sortPackagesDataByDateArrivedReg(data)
      // Filter out packages with unitedPackageId
      const filteredData = data.filter(p => !p.unitedPackageId)
      if (loggedUser.permissions['Worker']) return filteredData.filter(p => p.warehouse == loggedUser.UserAttributes['custom:warehouse'])
      return filteredData
    },
    enabled: !!userId,
  })
  const queryClient = useQueryClient()
  const { mutateAsync: deletePackageMutation } = useMutation({
    mutationFn: packageId => deletePackage(packageId),
    onMutate: packageId => {
      const old = queryClient.getQueryData([QUERY_KEYS.BACKOFFICE_USER_PACKAGES, userId])
      queryClient.setQueryData([QUERY_KEYS.BACKOFFICE_USER_PACKAGES, userId], old => old.filter(p => p.id !== packageId))
      return { old }
    },
    onError: (error, packageId, context) => {
      alert('Error deleting package')
      queryClient.setQueryData([QUERY_KEYS.BACKOFFICE_USER_PACKAGES, userId], context.old)
    },
  })

  const { mutateAsync: updatePackageMutation } = useMutation({
    mutationFn: item => updatePackage(item.id, item),
    onSuccess: (_m, item) => {
      queryClient.setQueryData([QUERY_KEYS.BACKOFFICE_USER_PACKAGES, userId], old => {
        return old?.map(p => (p.id === item.id ? item : p))
      })
    },
  })

  const { mutateAsync: updateUserMutation } = useMutation({
    mutationFn: updateUser,
    onMutate: item => {
      const old = queryClient.getQueryData([QUERY_KEYS.USER, userId])
      queryClient.setQueryData([QUERY_KEYS.USER, userId], item)
      setEditMode(false)
      return { old }
    },
    onSuccess: (_m, item) => {
      queryClient.setQueryData([QUERY_KEYS.USER, userId], item)
    },
    onError: (error, item, context) => {
      queryClient.setQueryData([QUERY_KEYS.USER, userId], context.old)
    },
  })
  const { mutateAsync: createPackageReqMutation } = useMutation({
    mutationFn: createPackageReq,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PACKAGE_REQUESTS], exact: false })
    },
  })
  const { mutateAsync: rejectPackageReqMutation } = useMutation({
    mutationFn: rejectPackageReq,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PACKAGE_REQUESTS], exact: false })
    },
  })
  const pkgReqs = useMemo(() => {
    return pkgReqMap ? Object.values(pkgReqMap).flat() : []
  }, [pkgReqMap])

  useEffect(() => {
    if (user) {
      updateUserState(user)
    }
  }, [user, updateUserState])

  const updateUserHandler = useCallback(async () => {
    try {
      await updateUserMutation(userState)
    } catch (error) {
      console.error('updateUser - error:', error)
      alert('error')
    }
  }, [updateUserMutation, userState])

  const onDeleteUser = useCallback(async () => {
    const confirmationId = window.prompt(
      `${packages.length > 0 ? 'There are packages associated with this user. ' : ''}Are you sure you want to delete the user? ${userState.email}? Copy & Paste the user ID to confirm: ${userState.sub}`
    )
    if (!confirmationId) return
    if (confirmationId !== userState.sub) {
      alert('wrong id')
      return
    }

    try {
      await deleteUser(userState.sub)
      alert('User deleted successfully')
      history.push('/office-e&u')
    } catch (error) {
      console.error('deleteUser - error:', error)
      alert(`Error deleting user: ${error.response?.data?.message || 'Unknown error'}`)
    }
  }, [history, packages?.length, userState.email, userState.sub])

  const confirmUserHandler = useCallback(async () => {
    try {
      await confirmUser(userState.sub)
      alert('User confirmed successfully')
      updateUserState({ ...userState, email_verified: 'true' })
    } catch (error) {
      console.error('confirmUser - error:', error)
      alert(`Error confirming user: ${error.response?.data?.message || 'Unknown error'}`)
    }
  }, [userState])

  const forceUpdateFunction = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BACKOFFICE_USER_PACKAGES, userId] })
  }, [queryClient, userId])

  const handleReq = useCallback(
    async (reqType, item) => {
      const isConfirmed = window.confirm('Are you sure you want to send this request?')
      if (!isConfirmed) return

      const data = {
        packageId: item.id,
        reqType,
      }

      //shrink-add notes. photo- no need nothing is added to req. return- redirect for payment.
      switch (reqType) {
        case 'shrinkRequest':
          data.shrinkNotes = window.prompt('Enter shrink notes for the warehouse')
          break
      }

      try {
        await createPackageReqMutation(data)
        alert('Request sent successfully')
      } catch (error) {
        const msg = error.response?.data?.msg || 'Error sending request'
        alert(msg)
        console.error(error)
      }
    },
    [createPackageReqMutation]
  )

  const handleCancelReq = useCallback(
    async pkgReq => {
      const isConfirmed = window.confirm('Are you sure you want to cancel this request?')
      if (!isConfirmed) return

      const data = {
        packageReqId: pkgReq.id,
        rejectReason: 'canceled',
      }

      try {
        await rejectPackageReqMutation(data)
        alert('Request cancelled successfully')
        return 'ok'
      } catch (error) {
        const msg = error.response?.data?.msg || 'Error cancelling request'
        alert(msg)
        console.error(error)
      }
    },
    [rejectPackageReqMutation]
  )

  const onDeletePackage = useCallback(
    async pkgToDelete => {
      const areActiveRequestExist = pkgReqs.some(
        req => req.packageId === pkgToDelete.id && !req.rejectedAt && !req.approvedAt && !req.closedAt
      )
      if (areActiveRequestExist) {
        const isConfirmed = window.confirm('This package has active requests. Are you sure you want to delete it?')
        if (!isConfirmed) return
      }
      const conf = window.prompt(`Copy & Paste the package ID to confirm delete: ${pkgToDelete.id}`)
      if (conf == null) return
      if (conf !== pkgToDelete.id) {
        alert('wrong id')
        return
      }
      try {
        await deletePackageMutation(pkgToDelete.id)
      } catch (error) {
        console.error(error)
        alert(error.data.response.msg)
      }
    },
    [deletePackageMutation, pkgReqs]
  )

  const isPackageSubtracted = useCallback(pkg => {
    if (pkg.delayStatus == '30' || pkg.delayStatus == 'SUBTRACTED' || pkg.delayStatus == 'DONATED') return true
    else return false
  }, [])

  const getStatus = useCallback(
    pkg => {
      if (pkg.isShipped) return 'Shipped'
      else if (isPackageSubtracted(pkg)) return 'Subtracted'
      else return pkg.isDraft ? 'At the Warehouse - DRAFT' : 'At the Warehouse'
    },
    [isPackageSubtracted]
  )

  const columnDefs = useUserColDefs({
    updatePackageMutation,
    forceUpdateFunction,
    setItemToUnite,
    setItemToReturn,
    handleReq,
    handleCancelReq,
    onDeletePackage,
    getStatus,
    pkgReqs,
    userPermissions: loggedUser.permissions,
    includeActions: true,
  })

  const menuItems = useMemo(() => {
    const res = editMode
      ? [
          {
            label: 'Save Changes',
            onClick: updateUserHandler,
          },
          {
            label: 'Cancel Edit',
            onClick: () => setEditMode(false),
          },
        ]
      : [
          {
            label: 'Edit User',
            onClick: () => setEditMode(true),
          },
        ]
    if (isSeniorAgent || isAgent) {
      res.push({
        className: 'danger',
        label: 'Delete User',
        onClick: onDeleteUser,
      })
      if (userState.email_verified !== 'true') {
        res.push({
          label: 'Confirm User Account',
          onClick: confirmUserHandler,
        })
      }
    }
    return res
  }, [editMode, updateUserHandler, isSeniorAgent, isAgent, onDeleteUser, userState.email_verified, confirmUserHandler])

  const pendingPkgReqs = useMemo(() => {
    return pkgReqs.filter(req => req.statusUpdates && req.statusUpdates[0].status === 'pending')
  }, [pkgReqs])
  const copyInput = (value, event) => {
    navigator.clipboard.writeText(value)
    // Find the input field in the same Form.Group and select its text
    const formGroup = event.target.closest('.form-group')
    if (formGroup) {
      const input = formGroup.querySelector('input')
      if (input) {
        // Temporarily enable the input if it's disabled
        const wasDisabled = input.disabled
        if (wasDisabled) {
          input.disabled = false
        }

        input.focus()
        input.select()

        // Re-disable the input if it was originally disabled
        if (wasDisabled) {
          input.disabled = true
        }
      }
    }
  }
  const isUserDeleted = userState.is_deleted === 'true'
  return (
    <div className='eau-user-page'>
      {!isLoading ? (
        <>
          <div className='eau-user-details' style={{ position: 'relative' }}>
            {!isUserDeleted && <DropdownMenu className='eau-user-dropdown' items={menuItems} />}
            {isUserDeleted && <p>User Deleted</p>}
            <div className='eau-user-details-row'>
              <Form.Group controlId='exampleForm.ControlInput1' style={{ flexGrow: '0.6', position: 'relative' }}>
                <Form.Label>User ID</Form.Label>
                <Form.Control disabled type='text' value={userState.sub} />
                {!editMode && <div className='copy-input' onClick={e => copyInput(userState.sub, e)} />}
              </Form.Group>

              <Form.Group style={{ flexGrow: '0.1', position: 'relative' }}>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  disabled={!editMode}
                  type='text'
                  value={userState.name}
                  onChange={e => {
                    updateUserState({ ...userState, name: e.target.value })
                  }}
                />
                {!editMode && <div className='copy-input' onClick={e => copyInput(userState.name, e)} />}
              </Form.Group>

              <Form.Group style={{ flexGrow: '0.1', position: 'relative' }}>
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  disabled={!editMode}
                  type='text'
                  value={userState.family_name}
                  onChange={e => {
                    updateUserState({ ...userState, family_name: e.target.value })
                  }}
                />
                {!editMode && <div className='copy-input' onClick={e => copyInput(userState.family_name, e)} />}
              </Form.Group>

              <Form.Group style={{ flexGrow: '0.4', position: 'relative' }}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  disabled
                  type='email'
                  value={userState.email}
                  onChange={e => {
                    updateUserState({ ...userState, email: e.target.value })
                  }}
                />
                {!editMode && <div className='copy-input' onClick={e => copyInput(userState.email, e)} />}
              </Form.Group>
            </div>
            <div className='eau-user-details-row'>
              <Form.Group style={{ position: 'relative' }}>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type='text'
                  value={userState['custom:adress']}
                  onChange={e => {
                    updateUserState({ ...userState, 'custom:adress': e.target.value })
                  }}
                  disabled={!editMode}
                />
                {!editMode && <div className='copy-input' onClick={e => copyInput(userState['custom:adress'], e)} />}
              </Form.Group>

              <Form.Group style={{ position: 'relative' }}>
                <Form.Label>Apartment Num</Form.Label>
                <Form.Control
                  type='text'
                  value={userState['custom:apartment']}
                  onChange={e => {
                    updateUserState({ ...userState, 'custom:apartment': e.target.value })
                  }}
                  disabled={!editMode}
                />
                {!editMode && <div className='copy-input' onClick={e => copyInput(userState['custom:apartment'], e)} />}
              </Form.Group>

              <Form.Group style={{ position: 'relative' }}>
                <Form.Label>City</Form.Label>
                <Form.Control
                  type='text'
                  value={userState['custom:city']}
                  onChange={e => {
                    updateUserState({ ...userState, 'custom:city': e.target.value })
                  }}
                  disabled={!editMode}
                />
                {!editMode && <div className='copy-input' onClick={e => copyInput(userState['custom:city'], e)} />}
              </Form.Group>

              <Form.Group style={{ position: 'relative' }}>
                <Form.Label>Postal Code</Form.Label>
                <Form.Control
                  type='text'
                  value={userState['custom:postalcode']}
                  onChange={e => {
                    updateUserState({ ...userState, 'custom:postalcode': e.target.value })
                  }}
                  disabled={!editMode}
                />
                {!editMode && <div className='copy-input' onClick={e => copyInput(userState['custom:postalcode'], e)} />}
              </Form.Group>

              <Form.Group style={{ position: 'relative' }}>
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type='text'
                  value={userState.phone_number}
                  onChange={e => {
                    updateUserState({ ...userState, phone_number: e.target.value })
                  }}
                  disabled={!editMode}
                />
                {!editMode && <div className='copy-input' onClick={e => copyInput(userState.phone_number, e)} />}
              </Form.Group>
            </div>
            {editMode && (
              <div className='eau-user-details-edit'>
                <img src={checkIcon} onClick={updateUserHandler} />
                <img src={cancelIcon} onClick={() => setEditMode(false)} />
              </div>
            )}
          </div>

          <div className='eau-table-cont'>
            <DataGrid
              rowData={packages || []}
              columnDefs={columnDefs}
              entityName='userPackages'
              rowHeight={76}
              loading={isPackagesLoading}
              getRowStyle={params => {
                const p = params.data
                let style = {}

                if (isPackageSubtracted(p)) {
                  style.color = 'red'
                }

                if (p.isDraft) {
                  style.backgroundColor = '#ffd0d0'
                }

                if (searchedPackage?.id === p.id) {
                  style.boxShadow = '0 0 10px rgba(0, 123, 255, 0.5)'
                  style.border = '2px solid #007bff'
                }
                if (p.isReturned) {
                  style.backgroundColor = '#df13136e'
                }
                return style
              }}
            />
          </div>
          {pendingPkgReqs.length > 0 && <PackageRequestDataGrid data={pendingPkgReqs} isLoading={isLoading} />}
          <ReturnPackageModal
            item={itemToReturn}
            createPackageReqMutation={createPackageReqMutation}
            handleClose={() => setItemToReturn(null)}
          />
          <UnitePackageModal
            show={!!itemToUnite}
            user={userState}
            pkgReqs={pkgReqs}
            handleClose={() => setItemToUnite(null)}
            selectedPackage={itemToUnite}
            allPackages={packages}
            updatePackage={updatePackageMutation}
          />
        </>
      ) : (
        <div className='spinner-cont1'>
          <Spinner animation='border' variant='primary' />
        </div>
      )}
    </div>
  )
}
